import type { Dispatch, SetStateAction } from "react";

import type { OpenAPI } from "simplydo/interfaces";

type AnnouncementSchema = OpenAPI.Schemas["announcements"];

/** Minimal user shape returned by UserChooser / used for custom recipients. */
export type ChoosableUser = Pick<
  OpenAPI.Schemas["users"],
  "_id" | "profile" | "emails"
> & { isInAudience?: boolean; isEmailInvitee?: boolean };

/** UI/API editing shape — POST body uses data/key on recipientConditions. */
export type AnnouncementRecipientCondition = {
  type?: string | null;
  data?: string | string[] | null;
  key?: string;
  value?: string;
};

export type AnnouncementListItem =
  OpenAPI.GET<"/{entity}/{id}/announcements">["response"]["automaticAnnouncements"][number];

export type AnnouncementCalendarEvent = NonNullable<
  AnnouncementSchema["calendarEvent"]
> & { organiserName?: string; organiserEmail?: string };

export type AnnouncementTeamsMeeting = NonNullable<
  AnnouncementSchema["teamsMeeting"]
>;

export type AnnouncementIdeaFieldCondition = {
  field?: string | null;
  condition?:
    | "isEmpty"
    | "isNotEmpty"
    | "equalTo"
    | "includes"
    | "doesNotInclude"
    | "notEqualTo";
  value?: string | null;
};

export type AnnouncementLaneCondition = {
  laneId?: string;
  value?: string;
  entityName?: string;
};

export type AnnouncementAssessmentCondition = { assessmentRoundId?: string };

export type AnnouncementSendWhenCondition =
  | AnnouncementIdeaFieldCondition
  | AnnouncementLaneCondition
  | AnnouncementAssessmentCondition;

export type AnnouncementSendWhenConditions = {
  conditionType?: "any" | "all";
  conditions?: AnnouncementSendWhenCondition[];
};

export type EditableAnnouncement = Omit<
  AnnouncementSchema,
  | "_id"
  | "user"
  | "createdAt"
  | "calendarEvent"
  | "teamsMeeting"
  | "sendWhenConditions"
  | "toUsers"
  | "sendAt"
> & {
  _id?: string | null;
  user?: string;
  createdAt?: string;
  calendarEvent?: AnnouncementCalendarEvent | null;
  teamsMeeting?: AnnouncementTeamsMeeting | null;
  sendWhenConditions?: AnnouncementSendWhenConditions;
  toUsers?: string[];
  sendAt?: string | null;
  recipientsMatched?: number;
  sendFailed?: boolean;
  failMessage?: string;
  failedAt?: string;
  referencesMissingField?: boolean;
  ownerToUsers?: Pick<OpenAPI.Schemas["users"], "_id" | "profile" | "emails">[];
  text?: string;
};

export type AnnouncementSubConditionOption = {
  _id?: string;
  id?: string;
  value?: string;
  text?: string;
  key?: string;
  name?: string;
  section?: string;
  title?: string;
  type?: string;
};

export type AnnouncementSubCondition = {
  key: string;
  type?: string;
  placeholder?: string;
  multiple?: boolean;
  getOptions?: (
    entityId: string,
    callback: (options: AnnouncementSubConditionOption[]) => void,
  ) => void;
};

export type AnnouncementConditionConfig = {
  value: string;
  text: string;
  subConditions?: AnnouncementSubCondition[];
};

export type AnnouncementSubconditionOptions = Record<
  string,
  AnnouncementSubConditionOption[]
>;

export type AnnouncementSetState = Dispatch<
  SetStateAction<EditableAnnouncement>
>;
export type AnnouncementListSetState = Dispatch<
  SetStateAction<EditableAnnouncement[]>
>;

type ApiAnnouncementLog =
  OpenAPI.GET<"/{entity}/{id}/announcements/logs">["response"]["logs"][number];

export type AnnouncementSentCondition = {
  type: string;
  data: string | string[];
  key: string;
  entityName?: string;
};

/** Log entry with runtime enrichment fields returned by the API. */
export type AnnouncementLog = ApiAnnouncementLog & {
  sentWhenConditions?: {
    conditionType?: string;
    conditions?: {
      field?: string;
      condition?: string;
      value?: string | null;
      fieldTitle?: string;
      entityName?: string;
      laneId?: string;
      assessmentRoundId?: string;
    }[];
  };
  replyTo?: string;
  callToActionText?: string;
  recipientUsers?: OpenAPI.Schemas["users"][];
  to?: string;
  attachmentName?: string;
  calendarEventName?: string;
  bccUsers?: OpenAPI.Schemas["users"][];
  entity?: string;
  sentConditions?: AnnouncementSentCondition[];
};

export type AnnouncementLogsResponse =
  OpenAPI.GET<"/{entity}/{id}/announcements/logs">["response"] & {
    automaticLogTypes?: string[];
  };

export type AnnouncementTemplate = OpenAPI.Schemas["announcementTemplates"];

export const EMPTY_ANNOUNCEMENT: EditableAnnouncement = {
  type: "standard",
  subject: "",
  body: "",
};

/** Whether the announcement draft should be treated as closed (no modal). */
export const isAnnouncementClosed = (
  announcement: EditableAnnouncement | null | undefined,
): boolean =>
  !announcement ||
  announcement === EMPTY_ANNOUNCEMENT ||
  (Object.keys(announcement).length === 0 && !announcement._id);

export type AnnouncementApiCreateBody =
  OpenAPI.POST<"/{entity}/{id}/announcements">["requestBody"];

/** Full create payload including runtime fields not yet in OpenAPI POST schema. */
export type AnnouncementCreatePayload = AnnouncementApiCreateBody & {
  sendWhenConditions?: AnnouncementSendWhenConditions;
  fullScreen?: boolean;
  toUserCondition?: EditableAnnouncement["toUserCondition"];
  toUsers?: string[];
};

export const toAnnouncementApiCreateBody = (
  payload: AnnouncementCreatePayload,
): AnnouncementApiCreateBody => {
  const { sendAt, recipientConditions, ...rest } = payload;
  return {
    ...rest,
    ...(sendAt ? { sendAt } : {}),
    ...(recipientConditions ? { recipientConditions } : {}),
  };
};

export const dropdownStringValue = (
  value: string | number | boolean | (string | number | boolean)[] | undefined,
): string | undefined => {
  if (value === undefined || value === null || Array.isArray(value)) {
    return undefined;
  }
  return String(value);
};

export const isSendAtValid = (sendAt: string | null | undefined): boolean =>
  !!sendAt && sendAt !== "" && isValidSendAtDate(sendAt);

const isValidSendAtDate = (sendAt: string): boolean => {
  const date = new Date(sendAt);
  return !Number.isNaN(date.getTime());
};
